import { ArrowRight, BadgeCheck, Building2, Clock3, Footprints, MapPin, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistance, formatMoney } from '../lib/api';
import type { HostelSummary } from '../lib/types';

export function HostelCard({ hostel }: { hostel: HostelSummary }) {
  const liveOffers = hostel.availableOfferCount > 0;
  const offerLabel = `${hostel.availableOfferCount} current agency listing${hostel.availableOfferCount === 1 ? '' : 's'}`;
  const agencyLabel = `${hostel.agencyCount} agenc${hostel.agencyCount === 1 ? 'y' : 'ies'}`;

  return (
    <article className="listing-card">
      <Link to={`/hostels/${hostel.slug}`} className="listing-card-link" aria-label={`View ${hostel.name} and its agency listings`}>
        <div className="listing-card-media">
          <img src={hostel.imageUrl} alt={hostel.name} loading="lazy" decoding="async" sizes="(max-width: 700px) 82vw, (max-width: 1120px) 50vw, 33vw" />
          <div className="listing-card-badges">
            <span className={`availability-badge ${liveOffers ? 'live' : 'pending'}`}><Clock3 size={13}/>{liveOffers ? offerLabel : 'Needs confirmation'}</span>
            {hostel.verificationStatus !== 'unverified' && <span className="facts-badge"><BadgeCheck size={14}/>Facts reviewed</span>}
            {hostel.imageSource === 'google' && <span className="facts-badge">Google photo</span>}
          </div>
        </div>

        <div className="listing-card-body">
          <div className="listing-card-title-row">
            <h3>{hostel.name}</h3>
            <span className="listing-rating"><Star size={13} fill="currentColor"/><strong>{hostel.averageRating ? hostel.averageRating.toFixed(1) : 'New'}</strong>{hostel.reviewCount > 0 && <small>({hostel.reviewCount})</small>}</span>
          </div>

          <p className="listing-location"><MapPin size={13}/><span>{hostel.landmark || hostel.campus.name}</span></p>
          {hostel.distanceMeters == null
            ? <div className="listing-proximity unavailable"><MapPin size={14}/><span>Walking route awaiting verification</span></div>
            : hostel.distanceKind === 'walking'
              ? <div className="listing-proximity"><Footprints size={15}/><strong>{Math.max(1, Math.round((hostel.walkingDurationSeconds ?? 0) / 60))} min walk</strong><span>{formatDistance(hostel.distanceMeters)} to campus reference</span></div>
              : <div className="listing-proximity unavailable"><MapPin size={14}/><span>{formatDistance(hostel.distanceMeters)} straight-line estimate</span></div>}

          <div className="listing-amenities">
            {hostel.amenities.slice(0, 2).map(item => <span key={item}>{item}</span>)}
            {hostel.amenities.length > 2 && <span>+{hostel.amenities.length - 2} more</span>}
          </div>

          <div className="listing-price">
            <span>Hostel price</span>
            <strong>{formatMoney(hostel.priceMin, hostel.currency)}</strong>
            <small>{hostel.priceMax && hostel.priceMax !== hostel.priceMin ? `up to ${formatMoney(hostel.priceMax, hostel.currency)} per academic year` : 'per academic year'}</small>
          </div>

          <footer className="listing-agency-footer">
            <span className="agency-footer-icon"><Building2 size={16}/></span>
            <span className="agency-footer-copy">
              <strong>{liveOffers ? offerLabel : 'No confirmed offer yet'}</strong>
              <small>{hostel.agencyCount ? `Available through ${agencyLabel}` : 'Ask matched agencies to check'}</small>
            </span>
            <span className="agency-footer-action">{liveOffers ? 'Compare' : 'View'} <ArrowRight size={14}/></span>
          </footer>
        </div>
      </Link>
    </article>
  );
}
